// Vendor Program v3 UAT recon (F9) — staff-editable ADVISORY qualification
// stages for the Vendor registry detail page. The 5 stages mirror Vendor
// Program v2.0 §1.5 (background / insurance / references / capability /
// W-9); codes MUST match the server catalog
// (VendorQualificationStageCatalog). Stages inform onboarding but gate
// NOTHING — no status transition checks them. The tab renders inside
// /portfolio-admin/vendors/:vendorId and reads the id from the route (the
// detail page passes no props).

import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card } from "@/design-system/components/ui";
import {
  formatCalendarDate,
  calendarDateInputToIso,
  truncate,
} from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  VENDOR_QUALIFICATION_STAGE_STATUSES,
  type VendorQualificationStageDto,
  type VendorQualificationStageStatus,
} from "@/api/portfolioAdmin/vendorQualificationsApi";
import {
  useUpsertVendorQualificationStage,
  useVendorQualificationStages,
} from "@/hooks/portfolioAdmin/useVendorQualifications";
// Stage list, status chip and the attribution line are shared with the
// Overview summary card so the two surfaces cannot drift.
import {
  pendingStage,
  StageStatusChip,
  stageRecordedLabel,
  VENDOR_QUALIFICATION_STAGES as STAGES,
} from "../vendorQualificationStages";
import { useDateFormatter } from "@/utils/datetime";

interface StageEditModalProps {
  vendorId: string;
  stageCode: string;
  stageTitle: string;
  stage: VendorQualificationStageDto;
  onClose: () => void;
}

function StageEditModal({
  vendorId,
  stageCode,
  stageTitle,
  stage,
  onClose,
}: StageEditModalProps) {
  const toast = useToast();
  const upsert = useUpsertVendorQualificationStage(vendorId);
  const [status, setStatus] = useState<VendorQualificationStageStatus>(stage.status);
  const [statusDate, setStatusDate] = useState(stage.statusDate?.slice(0, 10) ?? "");
  const [note, setNote] = useState(stage.note ?? "");
  const [evidenceUrl, setEvidenceUrl] = useState(stage.evidenceUrl ?? "");

  const onSave = async () => {
    try {
      await upsert.mutateAsync({
        stageCode,
        body: {
          status,
          statusDate: statusDate ? calendarDateInputToIso(statusDate) : null,
          note: note.trim() || null,
          evidenceUrl: evidenceUrl.trim() || null,
        },
      });
      toast.success("Stage updated.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Record stage — ${stageTitle}`}
      description="Advisory only — recording a stage never blocks a vendor status change."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={upsert.isPending}
            data-testid="vendor-qual-edit-save"
          >
            {upsert.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="vendor-qual-edit-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Status</span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={status}
            onChange={(e) => setStatus(e.target.value as VendorQualificationStageStatus)}
            data-testid="vendor-qual-edit-status"
          >
            {VENDOR_QUALIFICATION_STAGE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">As-of date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={statusDate}
            onChange={(e) => setStatusDate(e.target.value)}
            data-testid="vendor-qual-edit-date"
          />
        </label>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Note</span>
          <textarea
            className="min-h-[4rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="vendor-qual-edit-note"
          />
        </label>

        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Evidence link (https)
          </span>
          <input
            type="url"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={evidenceUrl}
            onChange={(e) => setEvidenceUrl(e.target.value)}
            placeholder="https://…"
            data-testid="vendor-qual-edit-url"
          />
        </label>
      </div>
    </Modal>
  );
}

export function VendorQualificationsTab() {
  const { tz } = useDateFormatter();
  const { vendorId } = useParams<{ vendorId: string }>();
  const auth = useAuth();
  const canEdit =
    auth.hasRoleCode("E1-VM") ||
    auth.hasRoleCode("E5") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Vendors.Update");
  const stagesQuery = useVendorQualificationStages(vendorId);
  const [editingCode, setEditingCode] = useState<string | null>(null);

  const byCode = new Map(
    (stagesQuery.data ?? []).map((s) => [s.stageCode, s] as const),
  );
  const editingStage = STAGES.find((s) => s.code === editingCode);

  return (
    <Card
      className="flex flex-col gap-space-3"
      data-testid="vendor-qualifications-tab"
    >
      <div className="flex flex-col gap-space-1">
        <h3 className="font-display font-bold text-h3 m-0">
          Qualification stages
        </h3>
        <p className="text-small text-gray-mid m-0">
          Advisory readiness checklist — these stages inform onboarding but do
          not block status changes.
        </p>
      </div>
      {stagesQuery.isLoading ? (
        <p className="text-small text-gray-mid italic m-0">
          Loading qualification stages…
        </p>
      ) : stagesQuery.isError ? (
        <p className="text-small text-status-error m-0">
          Could not load qualification stages.
        </p>
      ) : (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {STAGES.map((s) => {
            const stage = byCode.get(s.code) ?? pendingStage(s.code);
            return (
              <li
                key={s.code}
                className="border border-border-light rounded-md px-space-3 py-space-2 flex items-start justify-between gap-space-2"
                data-testid={`vendor-qual-stage-${s.code}`}
              >
                <div className="flex flex-col gap-space-1 min-w-0">
                  <span className="font-display font-bold text-small">
                    {s.title}
                  </span>
                  <span className="text-small text-gray-mid">{s.note}</span>
                  {stage.note ? (
                    <span
                      className="text-small text-onyx"
                      data-testid={`vendor-qual-note-${s.code}`}
                    >
                      {truncate(stage.note, 120)}
                    </span>
                  ) : null}
                  {stage.evidenceUrl ? (
                    <a
                      href={stage.evidenceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-small text-gold-shadow hover:underline"
                      data-testid={`vendor-qual-evidence-${s.code}`}
                    >
                      View evidence
                    </a>
                  ) : null}
                  {/* Who made this claim. Nothing but staff writes these
                      rows, so a status here is always someone's judgement —
                      the checklist should say whose. */}
                  {stageRecordedLabel(stage, tz) ? (
                    <span
                      className="text-caption text-gray-mid"
                      data-testid={`vendor-qual-recorded-by-${s.code}`}
                    >
                      {stageRecordedLabel(stage, tz)}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-space-2 shrink-0">
                  <div className="flex flex-col items-end gap-space-1">
                    <StageStatusChip status={stage.status} />
                    {stage.statusDate ? (
                      <span className="text-caption text-gray-mid">
                        As of {formatCalendarDate(stage.statusDate)}
                      </span>
                    ) : null}
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditingCode(s.code)}
                      data-testid={`vendor-qual-edit-${s.code}`}
                    >
                      Edit
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {canEdit && vendorId && editingCode && editingStage ? (
        <StageEditModal
          key={editingCode}
          vendorId={vendorId}
          stageCode={editingCode}
          stageTitle={editingStage.title}
          stage={byCode.get(editingCode) ?? pendingStage(editingCode)}
          onClose={() => setEditingCode(null)}
        />
      ) : null}
    </Card>
  );
}
